import {useDispatch} from 'react-redux';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {EntityType} from 'nutshell-graphql-types';
import type {
    UpdateCustomField as UpdateCustomFieldMutation,
    UpdateCustomFieldVariables,
    CustomFieldUpdateInput,
} from 'nutshell-graphql-types';

import {fetchSchemaAction} from '../../../../forms/forms-actions';

import UPDATE_CUSTOM_FIELD from '../mutations/update-custom-field.graphql';
import GET_CUSTOM_FIELD_STAGESETS from '../queries/get-custom-field-stagesets.graphql';
import GET_CUSTOM_FIELDS from '../queries/get-custom-fields.graphql';

export const useUpdateCustomField = (): {
    updateCustomField: (input: CustomFieldUpdateInput, entityType: EntityType) => any;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const dispatch = useDispatch();
    const [updateCustomField, {loading, error}] = useMutation<
        UpdateCustomFieldMutation,
        UpdateCustomFieldVariables
    >(UPDATE_CUSTOM_FIELD);

    return {
        updateCustomField: (input: CustomFieldUpdateInput, entityType: EntityType) => {
            return updateCustomField({
                // @ts-expect-error
                variables: {input, entityType},
                // Refetch the custom field stagesets query to update the cache
                // and therefore pipeline tables. Return of updateCustomField is
                // very different than the shape of the stageset query, so
                // updating the Apollo cache manually (read/writeQuery) would
                // require adding more info on update field return, and be more
                // error prone
                refetchQueries: [
                    {
                        query: GET_CUSTOM_FIELD_STAGESETS,
                    },
                ],
                update: (proxy, {data}) => {
                    const updatedCustomField =
                        data && data.updateCustomField && data.updateCustomField.customField;

                    if (!updatedCustomField) return;

                    let customFields = null;
                    try {
                        customFields = proxy.readQuery({
                            query: GET_CUSTOM_FIELDS,
                            variables: {input: {entityType}},
                        });
                    } catch {
                        return;
                    }

                    if (!customFields) return;

                    // Transform the field to match cache shape
                    const transformedField = {
                        ...updatedCustomField,
                        displayName: updatedCustomField.attribute.name,
                    } as const;

                    proxy.writeQuery({
                        query: GET_CUSTOM_FIELDS,
                        variables: {input: {entityType}},
                        data: {
                            customFields: {
                                // @ts-expect-error
                                ...customFields.customFields,
                                // @ts-expect-error
                                active: customFields.customFields.active.map((field) =>
                                    field.id === updatedCustomField.id ? transformedField : field
                                ),
                            },
                        },
                    });
                },
            }).then((result) => {
                // @ts-expect-error redux-thunk
                void dispatch(fetchSchemaAction());
                return result;
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
